import {FocusEvent, PropsWithChildren} from 'react';
import type {useForm, useFieldArray} from 'react-hook-form';

import {CreativeInfo} from 'client/creatives/utils/parse';

import {VideoService} from 'client/common/types';
import {
    Survey,
    SurveySlug,
    CreativeBox,
    BordomatikBox
} from 'client/common/types/survey';
import {CreativeCreateData} from 'client/creatives/types/creative';

export type VideoLinkProps = Pick<CreativeCreateData, 'id' | 'title'> & {
    service: VideoService;
};

export type CreativeFormProps = FormProps & {
    box: CreativeBox;
};

export type ChoiceType = 'single' | 'multi';
export interface CustomQuestionFormValue {
    label: string;
    type: ChoiceType;
    options: {text: string}[];
}

export type CreativeFormValues = FormValues & {
    options: {
        about: {text: string}[];
        image: {text: string}[];
    };
    customQuestions: CustomQuestionFormValue[];
};

export type BordomatikFormProps = FormProps & {
    box: BordomatikBox;
};

interface FormProps {
    slug: SurveySlug;
    state: Survey['state'];
    name: Survey['name'];
    disabled: boolean;
    creative: CreativeInfo;
    updateSurveyBoxContext: (box: unknown) => Promise<void>;
}

export type BordomatikFormValues = FormValues & {
    castingRole: string;
    options: {
        about: {text: string}[];
        image: {text: string}[];
        brand: {text: string}[];
        casting: {text: string}[];
        packshots: {text: string}[];
        slogans: {text: string}[];
    };
};

interface FormValues {
    creativeName: string;
    brandName: string;
    industry?: number;
}

export interface SettingsFormProps {
    box: CreativeBox | BordomatikBox;
    isPayed?: boolean;
    survey: Survey;
    disabled: boolean;
    creative: CreativeInfo;
    updateSurveyBoxContext: (box: unknown) => Promise<void>;
};

interface FormOptionListsProps {
    register: ReturnType<typeof useForm>['register'];
    errors: ReturnType<typeof useForm>['errors'];
    fieldName: string;
    onBlur: (num: number) => (e: FocusEvent<HTMLInputElement>) => void;
    rules: Record<string, unknown>;
}

type CommonOptionListsProps = PropsWithChildren<{
    fields: ReturnType<typeof useFieldArray>['fields'];
    disabled: boolean;
    placeholderPrefix?: string;
    minItems?: number;
    maxItems?: number;
    onAppend?: ReturnType<typeof useFieldArray>['append'];
    onRemove?: ReturnType<typeof useFieldArray>['remove'];
    addItemBtnText?: string;
    removeItemBtnTitle?: string;
    hasNoOpinion?: boolean;
}>;

export type OptionListsProps = FormOptionListsProps & CommonOptionListsProps;

export interface ImageListProps {
    register: ReturnType<typeof useForm>['register'];
    errors: ReturnType<typeof useForm>['errors'];
    fields: ReturnType<typeof useFieldArray>['fields'];
    fieldName: string;
    disabled: boolean;
    onBlur: (num?: number) => (imgSrc: string) => Promise<void>;
    rules: Record<string, unknown>;
    onRemove: ReturnType<typeof useFieldArray>['remove'];
    uploadBtnText: string;
    minItems: number;
    maxItems: number;
}

export enum UploadState {
    ready,
    progress,
    error,
    done
}
